import { AxiosError } from 'axios';

import Logger from 'utils/logger';
import { ProtectorType } from 'core/constants';
import { AnswerTypes } from 'utils/api/modules/incidents/incidents.types';
import { FormBuilderType, EnumOptions, EntityFieldType } from 'utils/api/modules/entities/entities.types';

import { EventDetailsType } from './EventDetails';

interface GetContentForKeyProps {
  form: FormBuilderType;
  content: Record<string, AnswerTypes>;
  key: string;
  sectionKey?: string;
}

export const findFieldByRoles = (
  fields: EntityFieldType[],
  roles: string[]
): [
  string,
  {
    uuid: string;
    key: string;
  },
][] =>
  fields
    .filter(({ properties }) => properties?.roles?.some((role: string) => roles.includes(role)))
    .map(({ key, uuid }) => [key, { uuid, key }]);

export const getContentForKey = ({
  form,
  content,
  key,
  sectionKey,
}: GetContentForKeyProps): AnswerTypes | undefined => {
  if (sectionKey) {
    const section = form.properties[sectionKey];
    const { questionType } = section.properties[key];
    if (questionType === ProtectorType.MultiChoice || questionType === ProtectorType.SingleChoice) {
      const enumOptions = section.properties[key].properties?.enum as unknown as EnumOptions;
      const keyToValueMap = enumOptions.reduce(
        (acc, item) => {
          acc[item.key] = item.value;
          return acc;
        },
        {} as Record<string, string>
      );

      const contentValue = content[key];
      if (!contentValue) {
        return undefined;
      }

      return (contentValue as string[]).map((k) => keyToValueMap[k]);
    }
  }

  return content[key];
};

export const getFieldKeysBasedOnProtectorType = (
  submission: EventDetailsType['submission'],
  protectorType: ProtectorType
): string[] => {
  if (!submission?.form?.form?.properties) {
    return [];
  }

  const attachmentKeys: string[] = [];
  const sections = submission.form.form.properties;

  // Iterate through each section
  Object.values(sections).forEach((section) => {
    if (section.properties) {
      // Iterate through each field in the section
      Object.entries(section.properties).forEach(([fieldKey, field]) => {
        if (field.questionType === protectorType) {
          attachmentKeys.push(fieldKey);
        }
      });
    }
  });

  return attachmentKeys;
};

export const checkIfAllAnswersInSectionAreEmpty = (
  form: FormBuilderType,
  sectionKey: string,
  content: Record<string, AnswerTypes>
): boolean => {
  const section = form.properties[sectionKey];
  return Object.keys(section.properties).every((property) => content[property] === undefined);
};

interface EventErrorData {
  entityFieldValues?: string[];
}

export const handleEditError = (
  error: AxiosError<string[] | EventErrorData>,
  details: EventDetailsType,
  enqueueErrorSnackbar: (message: string) => void
): string[] => {
  const errorFieldKeys: string[] = [];
  let errorMessage = 'The event could not be updated. Something went wrong.';
  const responseData = error.response?.data;

  if (responseData) {
    // If it's an array of error messages (most common case)
    if (Array.isArray(responseData)) {
      errorMessage = responseData[0] || errorMessage;
    }
    // If it's an object with field-specific errors
    else if (typeof responseData === 'object') {
      // Check for entityFieldValues (your current approach)
      if (responseData.entityFieldValues && Array.isArray(responseData.entityFieldValues)) {
        const fieldErrors = responseData.entityFieldValues;

        // Process each field error
        fieldErrors.forEach((fieldError: string) => {
          if (typeof fieldError === 'string') {
            // Try to extract field name and error message
            const parts = fieldError.split(' ');
            if (parts.length > 1) {
              const fieldKey = parts[0];
              const message = parts.slice(1).join(' ');

              // Collect field keys for highlighting
              errorFieldKeys.push(fieldKey);

              // Try to get the field name from the entity details
              let fieldName = fieldKey;
              if (details?.entity[fieldKey]?.schema?.name) {
                fieldName = details.entity[fieldKey].schema.name || fieldName;
              }

              // Show the original error message (don't change it)
              enqueueErrorSnackbar(`${fieldName} ${message}`);
            } else {
              enqueueErrorSnackbar(fieldError);
            }
          }
        });
        return errorFieldKeys; // Return field keys for form highlighting
      }

      // Handle other object-based error formats
      const errorValues = Object.values(responseData);
      if (errorValues.length > 0 && typeof errorValues[0] === 'string') {
        errorMessage = errorValues[0] || errorMessage;
      }
    } else if (typeof responseData === 'string') {
      errorMessage = responseData;
    }
  }

  // Show the error message
  enqueueErrorSnackbar(errorMessage);
  Logger.error('Failed to edit event', error);

  return errorFieldKeys;
};
